import { useState, useRef, useCallback, useEffect } from 'react';
import { Stage, Layer, Image as KImage, Text as KText, Line, Rect, Transformer } from 'react-konva';
import useImage from './useImage';
import EditorToolbar from './EditorToolbar';
import FilterPanel from './FilterPanel';
import TextPanel from './TextPanel';
import DrawPanel from './DrawPanel';
import StickerPanel from './StickerPanel';
import CropPanel from './CropPanel';
import VideoTrimPanel from './VideoTrimPanel';
import { Button } from '../ui/button';
import { X, Check, Undo2, Redo2 } from 'lucide-react';

const STAGE_WIDTH = 375;
const STAGE_HEIGHT = 667;

export default function CreatorEditor({ mediaUrl, mediaType, onSave, onCancel }) {
  const [activeTool, setActiveTool] = useState(null); // text, emoji, draw, stickers, filters, crop, trim
  const [elements, setElements] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [history, setHistory] = useState([[]]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const [lines, setLines] = useState([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawColor, setDrawColor] = useState('#ffffff');
  const [drawSize, setDrawSize] = useState(4);
  const [filters, setFilters] = useState({ brightness: 0, contrast: 0, saturation: 0, blur: 0, warmth: 0, vignette: 0 });
  const [filterPreset, setFilterPreset] = useState('none');
  const [zoom, setZoom] = useState(1);
  const [cropRatio, setCropRatio] = useState(null);
  const [trimRange, setTrimRange] = useState({ start: 0, end: 0 });
  const stageRef = useRef(null);
  const trRef = useRef(null);
  const [bgImage] = useImage(mediaUrl, 'anonymous');

  // Calculate scale to fit media in stage
  const getImageDimensions = () => {
    if (!bgImage) return { x: 0, y: 0, width: STAGE_WIDTH, height: STAGE_HEIGHT };
    const ratio = Math.min(STAGE_WIDTH / bgImage.width, STAGE_HEIGHT / bgImage.height);
    const w = bgImage.width * ratio;
    const h = bgImage.height * ratio;
    return { x: (STAGE_WIDTH - w) / 2, y: (STAGE_HEIGHT - h) / 2, width: w, height: h };
  };

  // History management
  const pushHistory = useCallback((newElements) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push([...newElements]);
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  }, [history, historyIndex]);

  const undo = () => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
      setElements(history[historyIndex - 1]);
    }
  };

  const redo = () => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(historyIndex + 1);
      setElements(history[historyIndex + 1]);
    }
  };

  // Attach transformer to selected node
  useEffect(() => {
    if (!selectedId || !trRef.current || !stageRef.current) return;
    const stage = stageRef.current;
    const node = stage.findOne('#' + selectedId);
    if (node) {
      trRef.current.nodes([node]);
      trRef.current.getLayer().batchDraw();
    }
  }, [selectedId]);

  // Add text element
  const addText = (config) => {
    const id = `text_${Date.now()}`;
    const newEl = {
      id, type: 'text',
      x: STAGE_WIDTH / 2 - 60, y: STAGE_HEIGHT / 2 - 20,
      text: config.text || 'Tap to edit',
      fontSize: config.fontSize || 24,
      fontFamily: config.fontFamily || 'Arial',
      fill: config.fill || '#ffffff',
      fontStyle: config.fontStyle || 'normal',
      align: 'center',
      draggable: true,
      rotation: 0,
      scaleX: 1, scaleY: 1,
      bgColor: config.bgColor || null,
    };
    const updated = [...elements, newEl];
    setElements(updated);
    pushHistory(updated);
    setSelectedId(id);
  };

  // Add emoji/sticker element
  const addEmoji = (emoji) => {
    const id = `emoji_${Date.now()}`;
    const newEl = {
      id, type: 'text',
      x: STAGE_WIDTH / 2 - 20, y: STAGE_HEIGHT / 2 - 20,
      text: emoji, fontSize: 48,
      fontFamily: 'Apple Color Emoji, Segoe UI Emoji',
      fill: '#ffffff',
      draggable: true, rotation: 0,
      scaleX: 1, scaleY: 1,
    };
    const updated = [...elements, newEl];
    setElements(updated);
    pushHistory(updated);
  };

  // Add sticker element
  const addSticker = (stickerConfig) => {
    const id = `sticker_${Date.now()}`;
    const newEl = {
      id, type: 'text',
      x: STAGE_WIDTH / 2 - 40, y: STAGE_HEIGHT / 2 - 15,
      text: stickerConfig.text || '#hashtag',
      fontSize: 16,
      fontFamily: 'Arial',
      fill: '#ffffff',
      draggable: true, rotation: 0,
      scaleX: 1, scaleY: 1,
      bgColor: stickerConfig.bgColor || 'rgba(0,0,0,0.6)',
      padding: 8,
      stickerType: stickerConfig.type,
    };
    const updated = [...elements, newEl];
    setElements(updated);
    pushHistory(updated);
  };

  // Drawing
  const handleMouseDown = (e) => {
    if (activeTool !== 'draw') return;
    setIsDrawing(true);
    const pos = e.target.getStage().getPointerPosition();
    setLines([...lines, { points: [pos.x, pos.y], color: drawColor, strokeWidth: drawSize }]);
  };

  const handleMouseMove = (e) => {
    if (!isDrawing || activeTool !== 'draw') return;
    const stage = e.target.getStage();
    const point = stage.getPointerPosition();
    const lastLine = lines[lines.length - 1];
    lastLine.points = lastLine.points.concat([point.x, point.y]);
    setLines([...lines.slice(0, -1), lastLine]);
  };

  const handleMouseUp = () => {
    setIsDrawing(false);
  };

  // Select element
  const handleSelect = (id) => {
    if (activeTool === 'draw') return;
    setSelectedId(id);
  };

  // Delete selected
  const deleteSelected = () => {
    if (!selectedId) return;
    const updated = elements.filter(el => el.id !== selectedId);
    setElements(updated);
    pushHistory(updated);
    setSelectedId(null);
  };

  // Update element
  const updateElement = (id, changes) => {
    const updated = elements.map(el => el.id === id ? { ...el, ...changes } : el);
    setElements(updated);
    pushHistory(updated);
  };

  // Apply CSS filters
  const getFilterStyle = () => {
    const f = filters;
    return {
      filter: [
        f.brightness !== 0 ? `brightness(${100 + f.brightness}%)` : '',
        f.contrast !== 0 ? `contrast(${100 + f.contrast}%)` : '',
        f.saturation !== 0 ? `saturate(${100 + f.saturation}%)` : '',
        f.blur > 0 ? `blur(${f.blur}px)` : '',
        f.warmth > 0 ? `sepia(${f.warmth}%)` : '',
      ].filter(Boolean).join(' ') || 'none',
    };
  };

  // Export
  const handleExport = () => {
    setSelectedId(null);
    setTimeout(() => {
      const stage = stageRef.current;
      if (!stage) return;
      const stageDataUrl = stage.toDataURL({ pixelRatio: 2 });

      // Apply CSS filters to exported image
      const f = filters;
      const hasFilters = f.brightness !== 0 || f.contrast !== 0 || f.saturation !== 0 || f.blur > 0 || f.warmth > 0;

      if (!hasFilters) {
        onSave(stageDataUrl, elements, filters);
        return;
      }

      const img = new window.Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        const filterStr = [
          f.brightness !== 0 ? `brightness(${100 + f.brightness}%)` : '',
          f.contrast !== 0 ? `contrast(${100 + f.contrast}%)` : '',
          f.saturation !== 0 ? `saturate(${100 + f.saturation}%)` : '',
          f.blur > 0 ? `blur(${f.blur}px)` : '',
          f.warmth > 0 ? `sepia(${f.warmth}%)` : '',
        ].filter(Boolean).join(' ');
        ctx.filter = filterStr;
        ctx.drawImage(img, 0, 0);
        onSave(canvas.toDataURL('image/png'), elements, filters);
      };
      img.src = stageDataUrl;
    }, 100);
  };

  // Deselect on empty click
  const handleStageClick = (e) => {
    if (e.target === e.target.getStage()) {
      setSelectedId(null);
    }
  };

  const imgDims = getImageDimensions();

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col" data-testid="creator-editor">
      {/* Top bar */}
      <div className="flex items-center justify-between px-3 py-2 bg-background/80 z-10">
        <Button variant="ghost" size="icon" onClick={onCancel} data-testid="editor-cancel">
          <X className="w-5 h-5" />
        </Button>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" onClick={undo} disabled={historyIndex === 0} data-testid="editor-undo">
            <Undo2 className="w-5 h-5" />
          </Button>
          <Button variant="ghost" size="icon" onClick={redo} disabled={historyIndex >= history.length - 1} data-testid="editor-redo">
            <Redo2 className="w-5 h-5" />
          </Button>
        </div>
        <Button onClick={handleExport} size="sm" className="bg-violet-600 hover:bg-violet-700 gap-1" data-testid="editor-done">
          <Check className="w-4 h-4" /> Done
        </Button>
      </div>

      {/* Canvas */}
      <div className="flex-1 flex items-center justify-center overflow-hidden" style={{...getFilterStyle(), transform: `scale(${zoom})`}}>
        <Stage
          ref={stageRef}
          width={STAGE_WIDTH}
          height={STAGE_HEIGHT}
          onMouseDown={handleMouseDown}
          onMousemove={handleMouseMove}
          onMouseup={handleMouseUp}
          onTouchStart={handleMouseDown}
          onTouchMove={handleMouseMove}
          onTouchEnd={handleMouseUp}
          onClick={handleStageClick}
          onTap={handleStageClick}
        >
          <Layer>
            {/* Background media */}
            {bgImage && (
              <KImage image={bgImage} {...imgDims} />
            )}
            {!bgImage && (
              <Rect x={0} y={0} width={STAGE_WIDTH} height={STAGE_HEIGHT} fill="#1a1a1a" />
            )}

            {/* Text/Emoji/Sticker elements */}
            {elements.map((el) => {
              if (el.type === 'text') {
                return (
                  <KText
                    key={el.id}
                    id={el.id}
                    x={el.x}
                    y={el.y}
                    text={el.text}
                    fontSize={el.fontSize}
                    fontFamily={el.fontFamily}
                    fill={el.fill}
                    fontStyle={el.fontStyle}
                    align={el.align || 'center'}
                    draggable={activeTool !== 'draw'}
                    rotation={el.rotation}
                    scaleX={el.scaleX}
                    scaleY={el.scaleY}
                    onClick={() => handleSelect(el.id)}
                    onTap={() => handleSelect(el.id)}
                    onDragEnd={(e) => {
                      updateElement(el.id, { x: e.target.x(), y: e.target.y() });
                    }}
                    onTransformEnd={(e) => {
                      const node = e.target;
                      updateElement(el.id, {
                        x: node.x(), y: node.y(),
                        rotation: node.rotation(),
                        scaleX: node.scaleX(),
                        scaleY: node.scaleY(),
                      });
                    }}
                  />
                );
              }
              return null;
            })}

            {/* Drawing lines */}
            {lines.map((line, i) => (
              <Line
                key={i}
                points={line.points}
                stroke={line.color}
                strokeWidth={line.strokeWidth}
                tension={0.5}
                lineCap="round"
                lineJoin="round"
                globalCompositeOperation="source-over"
              />
            ))}

            {/* Transformer for selected element */}
            {selectedId && (
              <Transformer
                ref={trRef}
                boundBoxFunc={(_, newBox) => {
                  const minSize = 10;
                  if (newBox.width < minSize || newBox.height < minSize) return _;
                  return newBox;
                }}
              />
            )}
          </Layer>
        </Stage>
      </div>

      {/* Active panel */}
      {activeTool === 'text' && (
        <TextPanel onAdd={addText} onClose={() => setActiveTool(null)}
          selectedElement={selectedId ? elements.find(e => e.id === selectedId) : null}
          onUpdate={(changes) => selectedId && updateElement(selectedId, changes)}
          onDelete={deleteSelected} />
      )}
      {activeTool === 'emoji' && (
        <div className="bg-card border-t border-border p-3 max-h-48 overflow-y-auto" data-testid="emoji-panel">
          <div className="grid grid-cols-8 gap-2 text-2xl text-center">
            {['😀','😂','🤣','😍','🥰','😎','🤩','🥳','🔥','💯','💪','🎵','🎶','🎤','🎧','🎸','🏆','👑','💎','⭐','❤️','💜','💙','🖤','👊','✌️','🤘','🙏','💰','🎯','⚡','🌟','😤','😈','🤑','💀','👀','🗣️','📢','🚀'].map(e => (
              <button key={e} onClick={() => addEmoji(e)}
                className="w-10 h-10 flex items-center justify-center hover:bg-muted/50 rounded-lg transition-colors">
                {e}
              </button>
            ))}
          </div>
        </div>
      )}
      {activeTool === 'draw' && (
        <DrawPanel color={drawColor} setColor={setDrawColor} size={drawSize} setSize={setDrawSize}
          onClear={() => setLines([])} onClose={() => setActiveTool(null)} />
      )}
      {activeTool === 'stickers' && (
        <StickerPanel onAdd={addSticker} onClose={() => setActiveTool(null)} />
      )}
      {activeTool === 'filters' && (
        <FilterPanel filters={filters} setFilters={setFilters}
          preset={filterPreset} setPreset={setFilterPreset} onClose={() => setActiveTool(null)} />
      )}
      {activeTool === 'crop' && (
        <CropPanel
          onCropChange={(ratio) => setCropRatio(ratio)}
          onZoomChange={(z) => setZoom(z)}
          zoom={zoom}
        />
      )}
      {activeTool === 'trim' && mediaType === 'video' && (
        <VideoTrimPanel
          videoUrl={mediaUrl}
          onTrimChange={(range) => setTrimRange(range)}
        />
      )}

      {/* Toolbar */}
      <EditorToolbar
        activeTool={activeTool}
        setActiveTool={setActiveTool}
        hasSelection={!!selectedId}
        onDelete={deleteSelected}
        mediaType={mediaType}
      />
    </div>
  );
}
